import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Pagination,
} from "@mui/material";
import {
  AddCircleOutline as AddIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import useCurrentFairWindow from "../../services/useCurrentFairWindow";
import useProductOfferings from "../../services/useProductOfferings";
import useAllProductOfferings from "../../services/useAllProductOfferings";
import useDuplicateOfferings from "../../services/useDuplicateOfferings";
import useBulkDeleteProductOfferings from "../../services/useBulkDeleteProductOfferings";
import useCoreProductCategories from "../../services/useCoreProductCategories";
import useFarmers from "../../services/useFarmers";
import ProductsSelector from "./ProductsSelector";
import OfferingsList from "./OfferingsList";

const PAGE_SIZE = 20;
type SortColumn = "product" | "producer";
type SortDirection = "asc" | "desc";

/**
 * Página principal de gerenciamento de ofertas de produtos para uma feira
 *
 * Funcionalidades:
 * - Visualizar ofertas existentes
 * - Adicionar produtos à feira
 * - Editar preço, custo, estoque
 * - Marcar como disponível/indisponível
 * - Remover ofertas
 * - Duplicar ofertas da feira anterior
 */
const FairOfferings = () => {
  const [showProductsSelector, setShowProductsSelector] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [confirmProducerDelete, setConfirmProducerDelete] = useState(false);
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<SortColumn>("product");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [producerFilter, setProducerFilter] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  const { data: fair, isLoading: isLoadingFair } = useCurrentFairWindow();
  const fairId = fair?.id ? parseInt(fair.id) : null;
  const orderingParam = `${sortDirection === "desc" ? "-" : ""}${sortColumn}`;
  const {
    data: offerings,
    isLoading: isLoadingOfferings,
    error: offeringsError,
  } = useProductOfferings(fairId, page, orderingParam, {
    search: searchTerm,
    producer: producerFilter,
    category: categoryFilter,
  });
  const { isLoading: isLoadingAllOfferings, error: allOfferingsError } =
    useAllProductOfferings(fairId, showProductsSelector);
  const duplicateMutation = useDuplicateOfferings();
  const bulkDeleteMutation = useBulkDeleteProductOfferings();
  const { data: categories } = useCoreProductCategories();
  const { data: farmers } = useFarmers();
  const selectedProducer = farmers?.find(
    (farmer) => farmer.id === producerFilter,
  );

  useEffect(() => {
    setPage(1);
  }, [fairId]);

  useEffect(() => {
    setPage(1);
  }, [categoryFilter, producerFilter, searchTerm]);

  const handleSortChange = (column: SortColumn) => {
    setPage(1);
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDirection("asc");
  };

  useEffect(() => {
    if (!offerings) return;
    const totalPages = Math.max(1, Math.ceil(offerings.count / PAGE_SIZE));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [offerings, page]);

  // Estatísticas das ofertas
  const stats = useMemo(() => {
    if (!offerings) return { total: 0, available: 0, outOfStock: 0 };

    return {
      total: offerings.count,
      available: offerings.results.filter((o) => o.available).length,
      outOfStock: offerings.results.filter(
        (o) => !o.unlimited_stock && o.stock === 0,
      ).length,
    };
  }, [offerings]);

  const handleDuplicateOfferings = () => {
    if (!fairId) return;

    duplicateMutation.mutate(fairId, {
      onSuccess: (data: any) => {
        setToast({
          type: "success",
          msg: `${data.duplicated_count} ofertas duplicadas com sucesso!`,
        });
        setConfirmDuplicate(false);
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Falha ao duplicar ofertas.";
        setToast({ type: "error", msg });
        setConfirmDuplicate(false);
      },
    });
  };

  const handleDeleteByProducer = () => {
    if (!fairId || !producerFilter) return;
    bulkDeleteMutation.mutate(
      { fairId, producerId: producerFilter },
      {
        onSuccess: (data: any) => {
          const count = data?.deleted_count ?? 0;
          setToast({
            type: "success",
            msg: `${count} produto${count !== 1 ? "s" : ""} removido${count !== 1 ? "s" : ""} do produtor.`,
          });
          setConfirmProducerDelete(false);
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            "Falha ao remover produtos do produtor.";
          setToast({ type: "error", msg });
          setConfirmProducerDelete(false);
        },
      },
    );
  };

  if (isLoadingFair) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!fair) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">
          Nenhuma feira ativa encontrada. Crie uma feira primeiro.
        </Alert>
      </Container>
    );
  }

  const isFairCancelled = fair.status === "cancelled";
  const isFairDelivered = fair.status === "delivered";
  const canEdit = !isFairCancelled && !isFairDelivered;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Produtos Oferecidos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Feira #{fair.id} -{" "}
            {new Date(fair.delivery_at || fair.open_at).toLocaleDateString(
              "pt-BR",
            )}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<DuplicateIcon />}
            onClick={() => setConfirmDuplicate(true)}
            disabled={
              !canEdit ||
              isLoadingOfferings ||
              duplicateMutation.isLoading ||
              (offerings && offerings.count > 0)
            }
          >
            Duplicar da feira anterior
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowProductsSelector(true)}
            disabled={!canEdit || isLoadingOfferings}
          >
            Adicionar Produtos
          </Button>
        </Stack>
      </Stack>

      {/* Status da feira */}
      {(isFairCancelled || isFairDelivered) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Esta feira está{" "}
          <strong>
            {fair.status === "cancelled" ? "cancelada" : "entregue"}
          </strong>
          . Não é possível editar as ofertas.
        </Alert>
      )}

      {/* Estatísticas */}
      {offerings && offerings.count > 0 && (
        <Stack direction="row" spacing={2} mb={3}>
          <Chip
            label={`${stats.total} produto${stats.total !== 1 ? "s" : ""}`}
            color="default"
            variant="outlined"
          />
          <Chip
            label={`${stats.available} disponíve${stats.available !== 1 ? "is" : "l"} na página`}
            color="success"
            variant="outlined"
          />
          {stats.outOfStock > 0 && (
            <Chip
              label={`${stats.outOfStock} sem estoque na página`}
              color="error"
              variant="outlined"
            />
          )}
        </Stack>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Filtros */}
      <Stack spacing={2} mb={3}>
        <TextField
          fullWidth
          label="Buscar por nome do produto ou produtor"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryFilter?.toString() || ""}
              onChange={(e: SelectChangeEvent) =>
                setCategoryFilter(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              label="Categoria"
            >
              <MenuItem value="">Todas</MenuItem>
              {categories?.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Produtor</InputLabel>
            <Select
              value={producerFilter?.toString() || ""}
              onChange={(e: SelectChangeEvent) =>
                setProducerFilter(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              label="Produtor"
            >
              <MenuItem value="">Todos</MenuItem>
              {farmers?.map((farmer) => (
                <MenuItem key={farmer.id} value={farmer.id}>
                  {farmer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {producerFilter && canEdit && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmProducerDelete(true)}
              disabled={isLoadingOfferings || bulkDeleteMutation.isLoading}
            >
              Remover ofertas do produtor
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Lista de ofertas */}
      {isLoadingOfferings ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : offeringsError ? (
        <Alert severity="error">
          Erro ao carregar ofertas. Tente recarregar a página.
        </Alert>
      ) : !offerings || offerings.count === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 2,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom color="text.secondary">
            Nenhum produto adicionado ainda
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Adicione produtos à feira ou duplique as ofertas da feira anterior.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="outlined"
              startIcon={<DuplicateIcon />}
              onClick={() => setConfirmDuplicate(true)}
              disabled={!canEdit || duplicateMutation.isLoading}
            >
              Duplicar da feira anterior
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowProductsSelector(true)}
              disabled={!canEdit}
            >
              Adicionar Produtos
            </Button>
          </Stack>
        </Box>
      ) : (
        <OfferingsList
          offerings={offerings.results}
          fairId={fairId!}
          canEdit={canEdit}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          onToast={(type, msg) => setToast({ type, msg })}
        />
      )}

      {offerings && offerings.count > PAGE_SIZE && (
        <Stack alignItems="center" mt={3}>
          <Pagination
            color="primary"
            page={page}
            count={Math.max(1, Math.ceil(offerings.count / PAGE_SIZE))}
            onChange={(_, newPage) => setPage(newPage)}
          />
        </Stack>
      )}

      {/* Dialog: Seletor de produtos */}
      <Dialog
        open={showProductsSelector}
        onClose={() => setShowProductsSelector(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Adicionar Produtos à Feira</DialogTitle>
        <DialogContent>
          {isLoadingAllOfferings ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : allOfferingsError ? (
            <Alert severity="error">
              Erro ao carregar produtos oferecidos. Tente novamente.
            </Alert>
          ) : (
            <ProductsSelector
              fairId={fairId!}
              existingOfferings={offerings?.results || []}
              onClose={() => setShowProductsSelector(false)}
              onSuccess={(count) => {
                setToast({
                  type: "success",
                  msg: `${count} produto${count !== 1 ? "s" : ""} adicionado${count !== 1 ? "s" : ""} com sucesso!`,
                });
                setShowProductsSelector(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar duplicação */}
      <Dialog
        open={confirmDuplicate}
        onClose={() => setConfirmDuplicate(false)}
      >
        <DialogTitle>Duplicar Ofertas?</DialogTitle>
        <DialogContent>
          <Typography>
            Isso irá copiar todos os produtos, preços, custos e configurações de
            estoque da feira anterior para esta feira.
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Você poderá editar as ofertas após a duplicação.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDuplicate(false)}
            disabled={duplicateMutation.isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDuplicateOfferings}
            variant="contained"
            disabled={duplicateMutation.isLoading}
          >
            {duplicateMutation.isLoading ? "Duplicando..." : "Duplicar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar remoção por produtor */}
      <Dialog
        open={confirmProducerDelete}
        onClose={() => setConfirmProducerDelete(false)}
      >
        <DialogTitle>Remover Ofertas do Produtor?</DialogTitle>
        <DialogContent>
          <Typography>
            Isso irá remover todas as ofertas do produtor{" "}
            <strong>{selectedProducer?.name || "selecionado"}</strong> nesta
            feira.
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmProducerDelete(false)}
            disabled={bulkDeleteMutation.isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteByProducer}
            color="error"
            variant="contained"
            disabled={bulkDeleteMutation.isLoading}
          >
            {bulkDeleteMutation.isLoading ? "Removendo..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast de feedback */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FairOfferings;
